/**
 * C5 of the V9→V7 CYOA authoring workstream (2026-06-07).
 * §3.J.D (2026-06-09): added the_branded_man_adventure as 5th V7 CYOA
 * scenario — Villains VG35011 supplement adventure section.
 *
 * Single source of truth for the 5 shippable V7 CYOA scenarios + the
 * localStorage-backed active-scenario selection. ScenarioRunner.tsx
 * reads the active scenario at module init via `getActiveScenario()`;
 * the Module Library drawer's Play button writes via
 * `setActiveScenarioId()` + forces a reload so the module-level
 * scenario const re-initializes.
 *
 * All 5 scenarios are eagerly imported so they're bundled together
 * (~125KB combined). Lazy chunking is possible but each scenario is
 * ~25KB and the player's first switch should be instant.
 */

import drNoScenarioData from '@seed/scenarios/dr_no_adventure.json';
import aViewToAKillScenarioData from '@seed/scenarios/a_view_to_a_kill_adventure.json';
import diamondsNeverDieScenarioData from '@seed/scenarios/diamonds_never_die_adventure.json';
import codeRedScenarioData from '@seed/scenarios/code_red_adventure.json';
import theBrandedManScenarioData from '@seed/scenarios/the_branded_man_adventure.json';
import fromRussiaWithLoveScenarioData from '@seed/scenarios/from_russia_with_love_adventure.json';
import fiveRingCircusScenarioData from '@seed/scenarios/five_ring_circus_adventure.json';
import forYourEyesOnlyScenarioData from '@seed/scenarios/for_your_eyes_only_adventure.json';
import futurumScenarioData from '@seed/scenarios/futurum_adventure.json';
import ghostWhispererScenarioData from '@seed/scenarios/ghost_whisperer_adventure.json';
import goldfingerIiScenarioData from '@seed/scenarios/goldfinger_ii_adventure.json';
import goldfingerScenarioData from '@seed/scenarios/goldfinger_adventure.json';
import liveAndLetDieScenarioData from '@seed/scenarios/live_and_let_die_adventure.json';
import livingDaylightsScenarioData from '@seed/scenarios/living_daylights_adventure.json';
import onHerMajestysSecretServiceSoloScenarioData from '@seed/scenarios/on_her_majestys_secret_service_solo_adventure.json';
import octopussyScenarioData from '@seed/scenarios/octopussy_adventure.json';
import musicOfMadnessScenarioData from '@seed/scenarios/music_of_madness_adventure.json';
import theManWithTheGoldenGunScenarioData from '@seed/scenarios/the_man_with_the_golden_gun_adventure.json';
import theMobiusStripScenarioData from '@seed/scenarios/the_mobius_strip_adventure.json';
import theSoundsOfDangerScenarioData from '@seed/scenarios/the_sounds_of_danger_adventure.json';
import youOnlyLiveTwiceScenarioData from '@seed/scenarios/you_only_live_twice_adventure.json';
import youOnlyLiveTwiceIiScenarioData from '@seed/scenarios/you_only_live_twice_ii_adventure.json';
import type { AdventureScenario } from './types/scenario';

export type ScenarioId =
  | 'dr_no_adventure'
  | 'a_view_to_a_kill_adventure'
  | 'diamonds_never_die_adventure'
  | 'code_red_adventure'
  | 'the_branded_man_adventure'
  | 'from_russia_with_love_adventure'
  | 'five_ring_circus_adventure'
  | 'for_your_eyes_only_adventure'
  | 'futurum_adventure'
  | 'ghost_whisperer_adventure'
  | 'goldfinger_ii_adventure'
  | 'goldfinger_adventure'
  | 'live_and_let_die_adventure'
  | 'living_daylights_adventure'
  | 'on_her_majestys_secret_service_solo_adventure'
  | 'octopussy_adventure'
  | 'music_of_madness_adventure'
  | 'the_man_with_the_golden_gun_adventure'
  | 'the_mobius_strip_adventure'
  | 'the_sounds_of_danger_adventure'
  | 'you_only_live_twice_adventure'
  | 'you_only_live_twice_ii_adventure';

export const SCENARIO_IDS: readonly ScenarioId[] = [
  'dr_no_adventure',
  'a_view_to_a_kill_adventure',
  'diamonds_never_die_adventure',
  'code_red_adventure',
  'the_branded_man_adventure',
  'from_russia_with_love_adventure',
  'five_ring_circus_adventure',
  'for_your_eyes_only_adventure',
  'futurum_adventure',
  'ghost_whisperer_adventure',
  'goldfinger_ii_adventure',
  'goldfinger_adventure',
  'live_and_let_die_adventure',
  'living_daylights_adventure',
  'on_her_majestys_secret_service_solo_adventure',
  'octopussy_adventure',
  'music_of_madness_adventure',
  'the_man_with_the_golden_gun_adventure',
  'the_mobius_strip_adventure',
  'the_sounds_of_danger_adventure',
  'you_only_live_twice_adventure',
  'you_only_live_twice_ii_adventure',
];

export const DEFAULT_SCENARIO_ID: ScenarioId = 'dr_no_adventure';

export const SCENARIO_REGISTRY: Record<ScenarioId, AdventureScenario> = {
  dr_no_adventure: drNoScenarioData as unknown as AdventureScenario,
  a_view_to_a_kill_adventure: aViewToAKillScenarioData as unknown as AdventureScenario,
  diamonds_never_die_adventure: diamondsNeverDieScenarioData as unknown as AdventureScenario,
  code_red_adventure: codeRedScenarioData as unknown as AdventureScenario,
  the_branded_man_adventure: theBrandedManScenarioData as unknown as AdventureScenario,
  from_russia_with_love_adventure: fromRussiaWithLoveScenarioData as unknown as AdventureScenario,
  five_ring_circus_adventure: fiveRingCircusScenarioData as unknown as AdventureScenario,
  for_your_eyes_only_adventure: forYourEyesOnlyScenarioData as unknown as AdventureScenario,
  futurum_adventure: futurumScenarioData as unknown as AdventureScenario,
  ghost_whisperer_adventure: ghostWhispererScenarioData as unknown as AdventureScenario,
  goldfinger_ii_adventure: goldfingerIiScenarioData as unknown as AdventureScenario,
  goldfinger_adventure: goldfingerScenarioData as unknown as AdventureScenario,
  live_and_let_die_adventure: liveAndLetDieScenarioData as unknown as AdventureScenario,
  living_daylights_adventure: livingDaylightsScenarioData as unknown as AdventureScenario,
  on_her_majestys_secret_service_solo_adventure: onHerMajestysSecretServiceSoloScenarioData as unknown as AdventureScenario,
  octopussy_adventure: octopussyScenarioData as unknown as AdventureScenario,
  music_of_madness_adventure: musicOfMadnessScenarioData as unknown as AdventureScenario,
  the_man_with_the_golden_gun_adventure: theManWithTheGoldenGunScenarioData as unknown as AdventureScenario,
  the_mobius_strip_adventure: theMobiusStripScenarioData as unknown as AdventureScenario,
  the_sounds_of_danger_adventure: theSoundsOfDangerScenarioData as unknown as AdventureScenario,
  you_only_live_twice_adventure: youOnlyLiveTwiceScenarioData as unknown as AdventureScenario,
  you_only_live_twice_ii_adventure: youOnlyLiveTwiceIiScenarioData as unknown as AdventureScenario,
};

/**
 * Human-readable display labels for each scenario (used by the
 * Module Library drawer's Play button + Stats drawer scenario header).
 */
export const SCENARIO_LABELS: Record<ScenarioId, string> = {
  dr_no_adventure: 'Dr. No (Core Rulebook Ch 20)',
  a_view_to_a_kill_adventure: 'A View to a Kill (VG35014)',
  diamonds_never_die_adventure: 'Diamonds Never Die (4Eyes)',
  code_red_adventure: 'Code Red (4Eyes)',
  the_branded_man_adventure: 'The Branded Man (Villains VG35011)',
  from_russia_with_love_adventure: 'From Russia With Love (Playtest 2003)',
  five_ring_circus_adventure: 'Five Ring Circus (4Eyes 2012)',
  for_your_eyes_only_adventure: 'For Your Eyes Only (VG36018)',
  futurum_adventure: 'Futurum (4Eyes 2018)',
  ghost_whisperer_adventure: 'Ghost Whisperer (4Eyes 2013)',
  goldfinger_ii_adventure: 'Goldfinger II (VG35012)',
  goldfinger_adventure: 'Goldfinger (VG35003)',
  live_and_let_die_adventure: 'Live And Let Die (VG35009)',
  living_daylights_adventure: 'The Living Daylights (fan)',
  on_her_majestys_secret_service_solo_adventure: "On Her Majesty's Secret Service — Solo (VG35015)",
  octopussy_adventure: 'Octopussy (VG35004)',
  music_of_madness_adventure: 'Music of Madness (Massconfusion 1984)',
  the_man_with_the_golden_gun_adventure: 'The Man With the Golden Gun (VG35013)',
  the_mobius_strip_adventure: 'The Möbius Strip (4Eyes 2013)',
  the_sounds_of_danger_adventure: 'The Sounds of Danger (DW#46, 1987)',
  you_only_live_twice_adventure: 'You Only Live Twice (VG35008)',
  you_only_live_twice_ii_adventure: 'You Only Live Twice II (VG35016)',
};

/**
 * Maps the V9 module_id values from `seed/modules/*.json` to their V7
 * CYOA scenario_id equivalents. Returns null when no V7 scenario exists
 * for the given module (current state: all 5 modules have V7 scenarios,
 * but the function keeps null-return as forward-compat for module-only
 * scenarios that ship without paragraphs).
 */
export function moduleIdToScenarioId(moduleId: string): ScenarioId | null {
  switch (moduleId) {
    case 'dr_no':
      return 'dr_no_adventure';
    case 'a_view_to_a_kill':
      return 'a_view_to_a_kill_adventure';
    case 'diamonds_never_die':
      return 'diamonds_never_die_adventure';
    case 'code_red':
      return 'code_red_adventure';
    case 'branded_man':
      return 'the_branded_man_adventure';
    case 'from_russia_with_love':
      return 'from_russia_with_love_adventure';
    case 'five_ring_circus':
      return 'five_ring_circus_adventure';
    case 'for_your_eyes_only':
      return 'for_your_eyes_only_adventure';
    case 'futurum':
      return 'futurum_adventure';
    case 'ghost_whisperer':
      return 'ghost_whisperer_adventure';
    case 'goldfinger_ii':
      return 'goldfinger_ii_adventure';
    case 'goldfinger':
      return 'goldfinger_adventure';
    case 'live_and_let_die':
      return 'live_and_let_die_adventure';
    case 'living_daylights':
      return 'living_daylights_adventure';
    case 'on_her_majestys_secret_service_solo':
      return 'on_her_majestys_secret_service_solo_adventure';
    case 'octopussy':
      return 'octopussy_adventure';
    case 'music_of_madness':
      return 'music_of_madness_adventure';
    case 'the_man_with_the_golden_gun':
      return 'the_man_with_the_golden_gun_adventure';
    case 'the_mobius_strip':
      return 'the_mobius_strip_adventure';
    case 'the_sounds_of_danger':
      return 'the_sounds_of_danger_adventure';
    case 'you_only_live_twice':
      return 'you_only_live_twice_adventure';
    case 'you_only_live_twice_ii':
      return 'you_only_live_twice_ii_adventure';
    default:
      return null;
  }
}

const ACTIVE_SCENARIO_LOCAL_STORAGE_KEY = 'briefing-room-active-scenario-id';

/**
 * Read the active scenario id from localStorage. Falls back to
 * DEFAULT_SCENARIO_ID when (a) no value is stored, (b) the stored value
 * isn't a known scenario id, or (c) localStorage isn't available
 * (private mode / SSR).
 */
export function getActiveScenarioId(): ScenarioId {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return DEFAULT_SCENARIO_ID;
  }
  try {
    const stored = localStorage.getItem(ACTIVE_SCENARIO_LOCAL_STORAGE_KEY);
    if (stored !== null && (SCENARIO_IDS as readonly string[]).includes(stored)) {
      return stored as ScenarioId;
    }
  } catch {
    // Quota / privacy mode — fall through to default.
  }
  return DEFAULT_SCENARIO_ID;
}

/**
 * Write the active scenario id to localStorage. Caller is responsible
 * for triggering a page reload after this so that ScenarioRunner's
 * module-level `scenario` re-initializes against the new id.
 */
export function setActiveScenarioId(id: ScenarioId): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(ACTIVE_SCENARIO_LOCAL_STORAGE_KEY, id);
  } catch {
    // Quota / privacy mode — best-effort only.
  }
}

/**
 * Convenience: returns the currently-active scenario data. Module-level
 * caller in ScenarioRunner.tsx invokes this once at import time.
 */
export function getActiveScenario(): AdventureScenario {
  return SCENARIO_REGISTRY[getActiveScenarioId()];
}
